import { useState, type ReactNode } from 'react'
import { Group, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { faFloppyDisk, faMinus, faPlus } from '@fortawesome/sharp-duotone-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ActionIcon } from '../../Common/ActionIcon'
import { Button } from '../../Common/Button'
import { StatusMessage } from '../../Common/StatusMessage'
import type { FieldStatus } from '../../../hooks/usePresentPositionEdit'

export interface EditableKeyDataRowProps {
  label: string
  value: string | null
  isDirty: boolean
  // Dirty *and* valid — the editor owns the reason and shows it, this only gates Save.
  canSave: boolean
  // There is a pending edit to discard — Cancel is disabled until then.
  canCancel: boolean
  status: FieldStatus
  message: string | null
  // When set, the row cannot be expanded and the reason is shown on hover (MSD-538).
  disabledReason?: string
  children: ReactNode
  onSave: () => void
  // Discards the pending edit; the row stays expanded so the reverted values are visible.
  onCancel: () => void
}

function getDisplayValue(value: string | null): { text: string; color?: string; italic?: boolean } {
  if (value == null) return { text: 'No Record', color: 'var(--link-color)', italic: true }
  if (value === '') return { text: 'N/A', color: 'var(--link-color)' }
  return { text: value }
}

export function EditableKeyDataRow({
  label,
  value,
  isDirty,
  canSave,
  canCancel,
  status,
  message,
  disabledReason,
  children,
  onSave,
  onCancel,
}: Readonly<EditableKeyDataRowProps>) {
  const [expanded, setExpanded] = useState(false)
  const display = getDisplayValue(value)
  const saving = status === 'saving'

  if (!expanded) {
    // data-disabled instead of disabled so the explanatory tooltip still fires.
    const editIcon = (
      <ActionIcon
        icon={faPlus}
        iconSize="sm"
        aria-label={`Edit ${label}`}
        aria-disabled={Boolean(disabledReason)}
        data-disabled={disabledReason ? true : undefined}
        onClick={() => {
          if (!disabledReason) setExpanded(true)
        }}
        style={{ flexShrink: 0 }}
      />
    )

    return (
      <Paper withBorder p="xs" shadow="xs" style={isDirty ? { backgroundColor: 'var(--unsaved-bg)' } : undefined}>
        {/* preventGrowOverflow is off so the value takes the width the label and icon
            leave behind instead of a fixed share of the row; the label keeps its own
            width and the value truncates into what remains. */}
        <Group justify="space-between" align="center" wrap="nowrap" gap="xs" preventGrowOverflow={false}>
          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Text fz="xs" fw="var(--mantine-font-weight-semibold)" style={{ whiteSpace: 'nowrap' }}>
              {label}:
            </Text>
            {isDirty && (
              <Text fz="xs" c="var(--unsaved-text)" fs="italic">
                Unsaved
              </Text>
            )}
          </Group>
          <Text
            fz="xs"
            truncate
            ta="right"
            c={display.color}
            fs={display.italic ? 'italic' : undefined}
            title={display.text}
            style={{ flex: 1, minWidth: 0 }}
          >
            {display.text}
          </Text>
          {disabledReason ? (
            <Tooltip label={disabledReason} withArrow multiline maw={220}>
              {editIcon}
            </Tooltip>
          ) : (
            editIcon
          )}
        </Group>
      </Paper>
    )
  }

  return (
    <Paper withBorder p="xs" shadow="xs">
      <Stack gap="xs">
        <Group justify="space-between" align="center" wrap="nowrap">
          {/* Same size/weight as the collapsed row and the read-only rows so expanding
              does not change the label's type scale. */}
          <Text fz="xs" fw="var(--mantine-font-weight-semibold)" style={{ whiteSpace: 'nowrap' }}>
            {label}:
          </Text>
          <Group gap={4} wrap="nowrap">
            <Button label="Cancel" size="xs" variant="outline" disabled={!canCancel || saving} onClick={onCancel} />
            <Button
              label="Save"
              size="xs"
              variant="filled"
              loading={saving}
              disabled={!canSave && !saving}
              onClick={onSave}
              rightSection={<FontAwesomeIcon icon={faFloppyDisk} />}
            />
            <ActionIcon
              icon={faMinus}
              iconSize="sm"
              aria-label={`Collapse ${label}`}
              onClick={() => setExpanded(false)}
            />
          </Group>
        </Group>
        {children}
        {/* saving/idle keep the plain body colour — only the two terminal states are coloured. */}
        {message &&
          (status === 'error' || status === 'success' ? (
            <StatusMessage status={status} fz="xs">
              {message}
            </StatusMessage>
          ) : (
            <Text fz="xs">{message}</Text>
          ))}
      </Stack>
    </Paper>
  )
}
